/**
 * Teen Patti Hand Evaluator - Dealer's Choice Variations
 * Handles wildcard substitutions and special evaluation modes
 */

// Card rank values for standard evaluation
const RANK_VALUES = { '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 };
const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const SUITS = ['hearts', 'diamonds', 'clubs', 'spades'];

// Hand type rankings (higher = better)
export const HAND_TYPES = {
  TRAIL: { rank: 6, name: 'Trail', description: 'Three of a Kind' },
  PURE_SEQUENCE: { rank: 5, name: 'Pure Sequence', description: 'Straight Flush' },
  SEQUENCE: { rank: 4, name: 'Sequence', description: 'Straight' },
  COLOR: { rank: 3, name: 'Color', description: 'Flush' },
  PAIR: { rank: 2, name: 'Pair', description: 'One Pair' },
  HIGH_CARD: { rank: 1, name: 'High Card', description: 'No combination' }
};

// ============ VARIATION DEFINITIONS ============

export const DEALER_CHOICE_VARIATIONS = {
  // Standard variations
  CLASSIC: {
    id: 'CLASSIC',
    name: 'Classic',
    description: 'Standard Teen Patti - highest hand wins',
    category: 'standard',
    wildcardType: null
  },
  MUFLIS: {
    id: 'MUFLIS',
    name: 'Muflis',
    description: 'Lowest hand wins! Rankings reversed.',
    category: 'standard',
    wildcardType: null,
    reversedRanking: true
  },
  
  // Wildcard/Joker variations
  AK47: {
    id: 'AK47',
    name: 'AK47',
    description: 'A, K, 4, 7 are wild jokers',
    category: 'wildcard',
    wildcardType: 'fixed',
    wildcardRanks: [14, 13, 4, 7] // A, K, 4, 7
  },
  SLUTTY_QUEENS: {
    id: 'SLUTTY_QUEENS',
    name: 'Slutty Queens',
    description: 'All Queens are wild - unless you have a King',
    category: 'wildcard',
    wildcardType: 'conditional_queens',
    // Queens are wild only if no King in hand
  },
  ONE_EYED_JACKS: {
    id: 'ONE_EYED_JACKS',
    name: 'One-Eyed Jacks',
    description: 'J♥ and J♠ are wild jokers',
    category: 'wildcard',
    wildcardType: 'fixed',
    wildcardCards: [
      { rank: 'J', suit: 'hearts' },
      { rank: 'J', suit: 'spades' }
    ]
  },
  LOWEST_CARD_JOKER: {
    id: 'LOWEST_CARD_JOKER',
    name: 'Lowest Card Joker',
    description: 'Your lowest card becomes a joker',
    category: 'wildcard',
    wildcardType: 'dynamic_lowest',
    wildcardCount: 1
  },
  TWO_LOWEST_JOKERS: {
    id: 'TWO_LOWEST_JOKERS',
    name: 'Two Lowest Jokers',
    description: 'Your two lowest cards become jokers',
    category: 'wildcard',
    wildcardType: 'dynamic_lowest',
    wildcardCount: 2
  },
  RANDOM_JOKER: {
    id: 'RANDOM_JOKER',
    name: 'Random Joker',
    description: 'Dealer reveals one random wild card',
    category: 'wildcard',
    wildcardType: 'random_revealed',
    wildcardCount: 1
  },
  NEXT_CARD_JOKER: {
    id: 'NEXT_CARD_JOKER',
    name: 'Next Card Joker',
    description: 'Card after the cut becomes wild',
    category: 'wildcard',
    wildcardType: 'next_card'
  },
  
  // Target number variations
  NINE_NINE_NINE: {
    id: 'NINE_NINE_NINE',
    name: '999',
    description: 'Closest to 999 wins! A=1, Face=0',
    category: 'target',
    targetMode: true,
    targetNumber: 999
  },
  
  // Community card variations
  COMMUNITY_1: {
    id: 'COMMUNITY_1',
    name: '1-Card Community',
    description: '2 hole cards + 1 shared community card',
    category: 'community',
    holeCards: 2,
    communityCards: 1
  },
  COMMUNITY_2: {
    id: 'COMMUNITY_2',
    name: '2-Card Community',
    description: '2 hole cards + 2 shared cards, pick best 3',
    category: 'community',
    holeCards: 2,
    communityCards: 2
  },
  COMMUNITY_3: {
    id: 'COMMUNITY_3',
    name: '3-Card Community',
    description: '2 hole cards + 3 shared cards, pick best 3',
    category: 'community',
    holeCards: 2,
    communityCards: 3
  }
};

// ============ HELPER FUNCTIONS ============

/**
 * Check if a card matches a wildcard specification
 */
const isWildcard = (card, variation, hand = null, revealedJoker = null) => {
  const config = DEALER_CHOICE_VARIATIONS[variation];
  if (!config || !config.wildcardType) return false;
  
  switch (config.wildcardType) {
    case 'fixed':
      // Check fixed rank wildcards (AK47)
      if (config.wildcardRanks) {
        return config.wildcardRanks.includes(card.value);
      }
      // Check specific card wildcards (One-Eyed Jacks)
      if (config.wildcardCards) {
        return config.wildcardCards.some(wc => 
          wc.rank === card.rank && wc.suit === card.suit
        );
      }
      return false;
    
    case 'conditional_queens':
      // Slutty Queens: All Queens are wild ONLY if no King in hand
      if (!hand) return false;
      const hasKing = hand.some(c => c.value === 13); // K = 13
      if (hasKing) return false; // Kings make Queens "loyal" - not wild
      return card.value === 12; // Q = 12, all Queens are wild
      
    case 'dynamic_lowest':
      // Lowest card(s) in hand are wild
      if (!hand) return false;
      const sortedByValue = [...hand].sort((a, b) => a.value - b.value);
      const lowestCards = sortedByValue.slice(0, config.wildcardCount);
      return lowestCards.some(c => c.id === card.id);
      
    case 'random_revealed':
    case 'next_card':
      // Card matches the revealed joker
      if (!revealedJoker) return false;
      return card.value === revealedJoker.value;
      
    default:
      return false;
  }
};

/**
 * Check if three values form a sequence
 */
const isSequence = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  // Regular sequence
  if (sorted[2] - sorted[1] === 1 && sorted[1] - sorted[0] === 1) return true;
  // A-2-3 sequence (special case: A can be low)
  if (sorted[0] === 2 && sorted[1] === 3 && sorted[2] === 14) return true;
  return false;
};

/**
 * Get sequence value for comparison (max ~800 to stay within band)
 * Rankings: A-K-Q highest, then A-2-3 (second), then K-Q-J, etc.
 */
const getSequenceValue = (values) => {
  const sorted = [...values].sort((a, b) => b - a);
  // A-K-Q is highest (800)
  if (sorted[0] === 14 && sorted[1] === 13 && sorted[2] === 12) return 800;
  // A-2-3 is second highest (750)
  if (sorted[0] === 14 && sorted[1] === 3 && sorted[2] === 2) return 750;
  // Other sequences ranked by high card (K-Q-J=700, Q-J-10=650, etc.)
  // Map high card: K(13)->700, Q(12)->650, J(11)->600, 10->550, 9->500, etc.
  return (sorted[0] - 2) * 50;
};

/**
 * Get tiebreaker value for flush/high card (max ~950 to stay within band)
 * Uses weighted sum: high*50 + mid*3 + low*0.2 (max = 14*50+13*3+12*0.2 = 700+39+2.4 ≈ 742)
 */
const getTiebreakerValue = (values) => {
  return Math.floor(values[0] * 50 + values[1] * 3 + values[2] * 0.2);
};

// ============ BASIC HAND EVALUATION ============

/**
 * Evaluate a 3-card hand without wildcards
 * Scoring bands: Trail(6000-6999) > PureSeq(5000-5999) > Seq(4000-4999) > Color(3000-3999) > Pair(2000-2999) > HighCard(1000-1999)
 */
export const evaluateBasicHand = (cards) => {
  if (!cards || cards.length !== 3) return null;
  
  const values = cards.map(c => c.value).sort((a, b) => b - a);
  const suits = cards.map(c => c.suit);
  const isFlush = suits[0] === suits[1] && suits[1] === suits[2];
  const isSeq = isSequence(values);
  
  // Trail (Three of a Kind) - Score: 6000 + card value (6002-6014)
  if (values[0] === values[1] && values[1] === values[2]) {
    return {
      type: HAND_TYPES.TRAIL,
      score: 6000 + values[0],
      cards: values,
      name: `Trail of ${cards[0].rank}s`
    };
  }
  
  // Pure Sequence (Straight Flush) - Score: 5000 + seq value (5000-5800)
  if (isFlush && isSeq) {
    return {
      type: HAND_TYPES.PURE_SEQUENCE,
      score: 5000 + getSequenceValue(values),
      cards: values,
      name: `Pure Sequence ${cards.map(c => c.rank).join('-')}`
    };
  }
  
  // Sequence (Straight) - Score: 4000 + seq value (4000-4800)
  if (isSeq) {
    return {
      type: HAND_TYPES.SEQUENCE,
      score: 4000 + getSequenceValue(values),
      cards: values,
      name: `Sequence ${cards.map(c => c.rank).join('-')}`
    };
  }
  
  // Color (Flush) - Score: 3000 + tiebreaker (3000-3750)
  if (isFlush) {
    return {
      type: HAND_TYPES.COLOR,
      score: 3000 + getTiebreakerValue(values),
      cards: values,
      name: `Color (${suits[0]})`
    };
  }
  
  // Pair - Score: 2000 + pair*50 + kicker (2000-2850)
  if (values[0] === values[1]) {
    return {
      type: HAND_TYPES.PAIR,
      score: 2000 + values[0] * 50 + values[2],
      cards: values,
      name: `Pair of ${RANKS[values[0] - 2]}s`
    };
  }
  if (values[1] === values[2]) {
    return {
      type: HAND_TYPES.PAIR,
      score: 2000 + values[1] * 50 + values[0],
      cards: values,
      name: `Pair of ${RANKS[values[1] - 2]}s`
    };
  }
  
  // High Card - Score: 1000 + tiebreaker (1000-1750)
  return {
    type: HAND_TYPES.HIGH_CARD,
    score: 1000 + getTiebreakerValue(values),
    cards: values,
    name: `High Card ${RANKS[values[0] - 2]}`
  };
};

// ============ WILDCARD EVALUATION ============

/**
 * Generate all possible card substitutions for wildcards
 * Returns the best possible hand
 */
const findBestWildcardHand = (cards, wildcardIndices) => {
  if (wildcardIndices.length === 0) {
    return evaluateBasicHand(cards);
  }
  
  let bestHand = null;
  
  // Generate all possible substitutions
  const generateSubstitutions = (cardsCopy, indices, idx) => {
    if (idx >= indices.length) {
      const hand = evaluateBasicHand(cardsCopy);
      if (!bestHand || (hand && hand.score > bestHand.score)) {
        bestHand = { ...hand, hasJokers: true };
      }
      return;
    }
    
    const wildcardIdx = indices[idx];
    const originalCard = cardsCopy[wildcardIdx];
    
    // Try all possible rank/suit combinations
    for (const rank of RANKS) {
      for (const suit of SUITS) {
        cardsCopy[wildcardIdx] = {
          ...originalCard,
          rank,
          suit,
          value: RANK_VALUES[rank]
        };
        generateSubstitutions([...cardsCopy], indices, idx + 1);
      }
    }
    
    // Restore original
    cardsCopy[wildcardIdx] = originalCard;
  };
  
  generateSubstitutions([...cards], wildcardIndices, 0);
  
  if (bestHand) {
    bestHand.name = `${bestHand.name} (Joker)`;
  }
  
  return bestHand || evaluateBasicHand(cards);
};

/**
 * Evaluate hand with wildcard variation
 */
export const evaluateWildcardHand = (cards, variation, revealedJoker = null) => {
  if (!cards || cards.length !== 3) return null;
  
  const config = DEALER_CHOICE_VARIATIONS[variation];
  if (!config || !config.wildcardType) {
    return evaluateBasicHand(cards);
  }
  
  // Find wildcard indices
  const wildcardIndices = [];
  cards.forEach((card, idx) => {
    if (isWildcard(card, variation, cards, revealedJoker)) {
      wildcardIndices.push(idx);
    }
  });
  
  // If all 3 cards are wild, return best possible (Trail of Aces)
  if (wildcardIndices.length === 3) {
    return {
      type: HAND_TYPES.TRAIL,
      score: 6000 + 14,
      cards: [14, 14, 14],
      name: 'Trail of Aces (All Jokers)',
      hasJokers: true
    };
  }
  
  // If 2 cards are wild, make Trail of the non-wild card
  if (wildcardIndices.length === 2) {
    const nonWildIdx = [0, 1, 2].find(i => !wildcardIndices.includes(i));
    const value = cards[nonWildIdx].value;
    return {
      type: HAND_TYPES.TRAIL,
      score: 6000 + value,
      cards: [value, value, value],
      name: `Trail of ${cards[nonWildIdx].rank}s (Jokers)`,
      hasJokers: true
    };
  }
  
  // 1 wildcard - find best hand
  return findBestWildcardHand(cards, wildcardIndices);
};

// ============ 999 TARGET EVALUATION ============

/**
 * Calculate 999 score for a hand
 * A=1, 2-9=face value, 10/J/Q/K=0
 * Each card is one digit, goal is closest to 999
 */
export const calculate999Score = (cards) => {
  if (!cards || cards.length !== 3) return null;
  
  const getDigit = (card) => {
    const val = card.value;
    if (val === 14) return 1; // Ace = 1
    if (val >= 10) return 0;  // 10, J, Q, K = 0
    return val;               // 2-9 = face value
  };
  
  // Get digits and sort descending to form highest possible number
  const digits = cards.map(getDigit).sort((a, b) => b - a);
  const number = digits[0] * 100 + digits[1] * 10 + digits[2];
  
  // Score is how close to 999 (higher = better)
  // A perfect 999 gets score 1000, 998 gets 999, etc.
  const distance = Math.abs(999 - number);
  const score = 1000 - distance;
  
  return {
    type: { rank: 0, name: '999', description: 'Target Number' },
    score,
    cards: digits,
    number,
    name: `${number} (${999 - distance} from 999)`,
    is999Mode: true
  };
};

// ============ COMMUNITY CARD EVALUATION ============

/**
 * Find the best 3-card hand from hole cards + community cards
 */
export const evaluateCommunityHand = (holeCards, communityCards, variation) => {
  if (!holeCards || holeCards.length < 2) return null;
  if (!communityCards || communityCards.length < 1) return null;
  
  const config = DEALER_CHOICE_VARIATIONS[variation];
  const allCards = [...holeCards, ...communityCards];
  
  // Generate all 3-card combinations
  const combinations = [];
  for (let i = 0; i < allCards.length; i++) {
    for (let j = i + 1; j < allCards.length; j++) {
      for (let k = j + 1; k < allCards.length; k++) {
        combinations.push([allCards[i], allCards[j], allCards[k]]);
      }
    }
  }
  
  // Find the best hand
  let bestHand = null;
  let bestCombo = null;
  
  for (const combo of combinations) {
    const hand = evaluateBasicHand(combo);
    if (!bestHand || (hand && hand.score > bestHand.score)) {
      bestHand = hand;
      bestCombo = combo;
    }
  }
  
  if (bestHand) {
    bestHand.selectedCards = bestCombo;
    bestHand.isCommunity = true;
  }
  
  return bestHand;
};

// ============ MAIN EVALUATION FUNCTION ============

/**
 * Master evaluation function - handles all variations
 */
export const evaluateHand = (cards, variation = 'CLASSIC', options = {}) => {
  const { revealedJoker, holeCards, communityCards } = options;
  const config = DEALER_CHOICE_VARIATIONS[variation];
  
  if (!config) {
    return evaluateBasicHand(cards);
  }
  
  // 999 Target Mode
  if (config.targetMode) {
    return calculate999Score(cards);
  }
  
  // Community Card Mode
  if (config.category === 'community' && holeCards && communityCards) {
    return evaluateCommunityHand(holeCards, communityCards, variation);
  }
  
  // Wildcard variations
  if (config.wildcardType) {
    const result = evaluateWildcardHand(cards, variation, revealedJoker);
    
    // Apply Muflis inversion if needed (some wildcards can combine with Muflis)
    if (config.reversedRanking && result) {
      result.score = 10000 - result.score;
      result.name = `${result.name} (Muflis)`;
    }
    
    return result;
  }
  
  // Standard evaluation
  const result = evaluateBasicHand(cards);
  
  // Apply Muflis inversion
  if (config.reversedRanking && result) {
    result.score = 10000 - result.score;
    result.name = `${result.name} (Muflis)`;
  }
  
  return result;
};

/**
 * Compare two hands - returns 1 if hand1 wins, -1 if hand2 wins, 0 if tie
 */
export const compareHands = (hand1, hand2) => {
  if (!hand1 && !hand2) return 0;
  if (!hand1) return -1;
  if (!hand2) return 1;
  
  if (hand1.score > hand2.score) return 1;
  if (hand1.score < hand2.score) return -1;
  return 0;
};

/**
 * Mark wildcards in a hand based on variation
 */
export const markWildcards = (cards, variation, revealedJoker = null) => {
  const config = DEALER_CHOICE_VARIATIONS[variation];
  if (!config || !config.wildcardType) {
    return cards.map(c => ({ ...c, isJoker: false }));
  }
  
  return cards.map(card => ({
    ...card,
    isJoker: isWildcard(card, variation, cards, revealedJoker)
  }));
};

/**
 * Get variation categories for UI grouping
 */
export const getVariationsByCategory = () => {
  const categories = {
    standard: [],
    wildcard: [],
    target: [],
    community: []
  };
  
  Object.values(DEALER_CHOICE_VARIATIONS).forEach(v => {
    if (categories[v.category]) {
      categories[v.category].push(v);
    }
  });
  
  return categories;
};

export default {
  HAND_TYPES,
  DEALER_CHOICE_VARIATIONS,
  evaluateHand,
  evaluateBasicHand,
  evaluateWildcardHand,
  calculate999Score,
  evaluateCommunityHand,
  compareHands,
  markWildcards,
  getVariationsByCategory,
  isWildcard
};
